import {
  CanvasTexture,
  Material,
  MathUtils,
  Mesh,
  Object3D,
  Raycaster,
  Sprite,
  SpriteMaterial,
  Vector2
} from "three";
import { addBasicObjects } from "./addBasicObjects";
import { createAmbientFishLayer } from "./AmbientFish";
import { SceneProps } from "./SceneProps";
import { TubeModel, type TubeWorldParticleStream } from "./TubeModel";
import { animate } from "@/lib/animate";
import { startTubeFlowSound, stopTubeFlowSound } from "@/lib/game-audio";
import { useSceneStore } from "@/lib/sceneStore";
import { TubeBacteriesModel } from "./TubeBacteriesModel";
import { getSceneRevealStages } from "./SceneReveal";
import { getPollutionLevelFromCounts } from "./PollutionEffects";
import {
  disposeGarbageObject,
  getGarbageVerticalOffset,
  getGarbageRoot,
  showGarbageCollectionFeedback,
  setupGarbageObject,
  updateFloatingGarbageObject
} from "./GarbageObjects";
import { createGarbageHintTracker } from "./GarbageHintTracker";

const ANIMATION_DURATION = 500;
const WASTE_WATER_HIT_COOLDOWN_MS = 14;
const TUBE_AIM_HOLD_DURATION_MS = 0;
const TUBE_FLOW_GRACE_PERIOD_MS = 520;
const TUBE_HANDLE_START_ANIMATION_MS = 260;
const TUBE_HANDLE_STOP_ANIMATION_MS = 220;
const TUBE_HANDLE_ROTATION_DEG = 82;
const TUBE_BODY_BASE_PITCH_DEG = 15;
const TUBE_BODY_BASE_Y = -0.6;
const TUBE_BODY_BASE_Z = -1.25;
const CATFISH_TUBE_SCALE = 1.06;
const TUBE_RETICLE_Y = -0.08;
const TUBE_RETICLE_BASE_SCALE = 0.19;
const TUBE_FALLBACK_BODY_TWIST_DEG = 8;
const WASH_AWAY_ANIMATION_DURATION = 1050;
const CATFISH_GARBAGE_MIN_Y = 0.58;
const CATFISH_GARBAGE_MAX_Y = 2.45;
const CATFISH_GARBAGE_SPAWN_CENTER_Z = -0.45;
const CATFISH_GARBAGE_ROOM_RADIUS = 2.95;
const CATFISH_GARBAGE_MAX_NEAR_CAMERA_Z = -0.72;
const CATFISH_PERIPHERAL_GARBAGE_SCALE = 0.82;
const CATFISH_GARBAGE_YAW_LANES = [-1.18, -0.78, -0.39, 0, 0.39, 0.78, 1.18];
const CATFISH_GARBAGE_DEPTH_RADII = [1.58, 2.18, 2.78];
const TUBE_HANDLE_OBJECT_NAMES = [
  "Bone",
  "Bone001",
  "Bone.001",
  "handle",
  "Handle",
  "tube_handle",
  "TubeHandle",
  "valve",
  "Valve",
  "lever",
  "Lever",
  "tap",
  "Tap",
  "Cube",
];

function createTubeReticle() {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.shadowColor = "rgba(85, 255, 210, 0.85)";
    context.shadowBlur = 12;
    context.strokeStyle = "rgba(255,255,255,0.98)";
    context.lineWidth = 8;
    context.beginPath();
    context.arc(64, 64, 31, 0, Math.PI * 2);
    context.stroke();

    context.shadowBlur = 18;
    context.strokeStyle = "rgba(86,255,164,0.98)";
    context.lineWidth = 6;
    context.beginPath();
    context.moveTo(64, 12);
    context.lineTo(64, 35);
    context.moveTo(64, 93);
    context.lineTo(64, 116);
    context.moveTo(12, 64);
    context.lineTo(35, 64);
    context.moveTo(93, 64);
    context.lineTo(116, 64);
    context.stroke();
  }

  const texture = new CanvasTexture(canvas);
  const material = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  });
  const sprite = new Sprite(material);
  sprite.renderOrder = 1_000_220;
  sprite.position.set(0, TUBE_RETICLE_Y, -1.12);
  sprite.scale.setScalar(TUBE_RETICLE_BASE_SCALE);
  sprite.layers.set(1);

  return {
    sprite,
    update: (timeSeconds: number, isAiming: boolean) => {
      const wave = (Math.sin(timeSeconds * (isAiming ? 12 : 4.5)) + 1) * 0.5;
      const pulse = isAiming ? 1 + wave * 0.14 : 1 + wave * 0.045;

      sprite.scale.setScalar(TUBE_RETICLE_BASE_SCALE * pulse);
      material.opacity = isAiming ? 0.72 + wave * 0.28 : 0.72 + wave * 0.14;
    },
    dispose: () => {
      material.dispose();
      texture.dispose();
    }
  };
}

function findTubeHandleObject(root: Object3D): Object3D | null {
  for (const name of TUBE_HANDLE_OBJECT_NAMES) {
    const object = root.getObjectByName(name);

    if (object) return object;
  }

  return null;
}

function getCatfishGarbageHeight(index: number, count: number) {
  const safeCount = Math.max(count, 1);
  const bandProgress = ((index * 5) % safeCount) / Math.max(safeCount - 1, 1);
  const wave = Math.sin(index * 1.71) * 0.12;

  return MathUtils.clamp(
    MathUtils.lerp(CATFISH_GARBAGE_MIN_Y, CATFISH_GARBAGE_MAX_Y, bandProgress) +
      wave,
    CATFISH_GARBAGE_MIN_Y,
    CATFISH_GARBAGE_MAX_Y
  );
}

function setCatfishGarbagePosition(
  object: Object3D,
  index: number,
  count: number,
  garbageKey: string
) {
  const safeCount = Math.max(count, 1);
  const laneIndex = index % CATFISH_GARBAGE_YAW_LANES.length;
  const depthBand =
    Math.floor(index / CATFISH_GARBAGE_YAW_LANES.length) %
    CATFISH_GARBAGE_DEPTH_RADII.length;
  const isPeripheral =
    depthBand === CATFISH_GARBAGE_DEPTH_RADII.length - 1 &&
    (laneIndex === 0 || laneIndex === CATFISH_GARBAGE_YAW_LANES.length - 1);
  const yaw =
    CATFISH_GARBAGE_YAW_LANES[laneIndex] +
    (depthBand - 1) * 0.045 +
    Math.sin(index * 2.17) * 0.035;
  const radius =
    CATFISH_GARBAGE_DEPTH_RADII[depthBand] +
    Math.sin(index * 1.33) * 0.04;
  const x = Math.sin(yaw) * radius;
  const z = Math.min(
    CATFISH_GARBAGE_SPAWN_CENTER_Z - Math.cos(yaw) * radius,
    CATFISH_GARBAGE_MAX_NEAR_CAMERA_Z
  );
  const distanceFromRoomCenter = Math.hypot(x, z);
  const roomScale =
    distanceFromRoomCenter > CATFISH_GARBAGE_ROOM_RADIUS
      ? CATFISH_GARBAGE_ROOM_RADIUS / distanceFromRoomCenter
      : 1;

  object.position.set(
    x * roomScale,
    getCatfishGarbageHeight(index, safeCount) +
      getGarbageVerticalOffset(garbageKey),
    z * roomScale
  );
  object.userData.catfishPeripheralGarbage = isPeripheral;
}

function animateWashAwayGarbage(
  object: Object3D,
  scene: Object3D,
  onComplete: () => void
) {
  const startPosition = object.position.clone();
  const endPosition = startPosition.clone();
  endPosition.y += 0.28;
  endPosition.x += (Math.random() - 0.5) * 0.16;
  const startScale = object.scale.clone();
  const clonedMaterials: Material[] = [];

  object.traverse((child) => {
    if (!(child instanceof Mesh)) return;

    if (Array.isArray(child.material)) {
      child.material = child.material.map((material) => {
        const clone = material.clone();
        clone.transparent = true;
        clone.depthWrite = false;
        clonedMaterials.push(clone);
        return clone;
      });
    } else {
      const clone = child.material.clone();
      clone.transparent = true;
      clone.depthWrite = false;
      child.material = clone;
      clonedMaterials.push(clone);
    }
  });

  animate({
    duration: WASH_AWAY_ANIMATION_DURATION,
    onUpdate: (progress) => {
      const p = MathUtils.clamp(progress, 0, 1);
      const eased = p * p * (3 - 2 * p);
      object.position.lerpVectors(startPosition, endPosition, eased);
      object.scale.copy(startScale).multiplyScalar(MathUtils.lerp(1, 0.72, eased));
      object.rotation.y += 0.035;
      object.rotation.x += 0.018;
      clonedMaterials.forEach((material) => {
        material.opacity = Math.pow(1 - p, 1.18) * 0.78;
      });
    },
    onComplete: () => {
      scene.remove(object);
      clonedMaterials.forEach((material) => material.dispose());
      onComplete();
    }
  });
}

export const startCatfishScene = (props: SceneProps) => {
  const {
    scene,
    camera,
    models,
    onWin,
    weaponModel,
    garbageModels
  } = props;

  const { currentGameId, totalGarbageCount: totalCount } =
    useSceneStore.getState();
  const shouldWashAwayGarbage = currentGameId === "iniya";

  const roomSize = 5;
  const baseCameraHeight = 2;
  const sceneStartedAt = performance.now();
  let lastGarbageUpdateTime = performance.now();
  const getAmbientRevealProgress = () =>
    getSceneRevealStages(props.sceneReveal?.getProgress() ?? 1).decor;
  const getAmbientPollutionLevel = () => {
    const { collectedGarbageCount, totalGarbageCount } =
      useSceneStore.getState();
    return getPollutionLevelFromCounts(collectedGarbageCount, totalGarbageCount);
  };

  const { onExit, onUpdate, hudGroup } = addBasicObjects(props);
  const ambientFish = createAmbientFishLayer(props, {
    roomSize,
    baseCameraHeight,
  });

  const stick = models[weaponModel.key].getModel();

  if (!stick) return { onExit: () => {}, onUpdate: () => {} };

  stick.position.set(0, TUBE_BODY_BASE_Y, TUBE_BODY_BASE_Z);
  if (currentGameId === "catfish") {
    stick.scale.multiplyScalar(CATFISH_TUBE_SCALE);
  }
  const tubeHandle = findTubeHandleObject(stick);
  const animatedTubePart = tubeHandle ?? stick;
  const baseHandleRotation = animatedTubePart.rotation.clone();
  let fallbackBodyTwist = 0;

  stick.layers.set(1);

  stick.renderOrder = 1_000_200;
  stick.traverse((child) => {
    child.layers.set(1);
  });

  hudGroup.add(stick);
  const reticle = createTubeReticle();
  hudGroup.add(reticle.sprite);

  const tubeParticles: TubeWorldParticleStream | null =
    weaponModel instanceof TubeBacteriesModel || weaponModel instanceof TubeModel
      // Поток считает позиции через getWorldPosition(), поэтому держим Points
      // в root scene, а не внутри сдвинутого gameGroup.
      ? weaponModel.createWorldParticleStream(props.rootScene ?? scene)
      : null;

  const objects = new Set<Object3D>();
  let objectsArray: Object3D[] = [];
  const garbageHintTracker = createGarbageHintTracker(objects, camera);

  const goods = garbageModels.map((m) => models[m.key]);

  for (let i = 0; i <= totalCount - 1; i++) {
    const model = goods[i % goods.length];
    const object = model.getModel();
    if (!object) continue;

    setCatfishGarbagePosition(object, i, totalCount, model.key);
    object.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    setupGarbageObject(object, model.key);
    if (object.userData.catfishPeripheralGarbage === true) {
      object.scale.multiplyScalar(CATFISH_PERIPHERAL_GARBAGE_SCALE);
    }
    if (shouldWashAwayGarbage) {
      object.scale.multiplyScalar(1.4);
    }
    object.userData.washAwayOnHit = shouldWashAwayGarbage;
    objects.add(object);
    objectsArray.push(object);
    scene.add(object);
  }

  const raycaster = new Raycaster();
  const screenCenter = new Vector2(0, 0);

  const plusOneCanvas = document.createElement("canvas");
  plusOneCanvas.width = 100;
  plusOneCanvas.height = 100;
  const plusOneCtx = plusOneCanvas.getContext("2d");
  if (plusOneCtx) {
    plusOneCtx.font = "bold 60px Arial";
    plusOneCtx.fillStyle = "#ffffff";
    plusOneCtx.textAlign = "center";
    plusOneCtx.textBaseline = "middle";
    plusOneCtx.fillText("+1", 50, 50);
  }
  const plusOneTexture = new CanvasTexture(plusOneCanvas);
  const plusOneMaterial = new SpriteMaterial({
    map: plusOneTexture,
    opacity: 0.5,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  let aimedRoot: Object3D | null = null;
  let aimStartedAtMs = 0;
  let tubeState: "idle" | "emitting" | "closing" = "idle";
  let tubeAnimationToken = 0;
  let flowGraceUntilMs = 0;

  const stopTubeFlow = (smooth = true) => {
    if (smooth && tubeState === "closing") return;

    aimedRoot = null;
    aimStartedAtMs = 0;
    flowGraceUntilMs = 0;
    tubeAnimationToken += 1;
    stopTubeFlowSound();

    if (!smooth || tubeState === "idle") {
      tubeState = "idle";
      fallbackBodyTwist = 0;
      animatedTubePart.rotation.copy(baseHandleRotation);
      return;
    }

    tubeState = "closing";
    const token = tubeAnimationToken;
    const startX = animatedTubePart.rotation.x;
    const startY = animatedTubePart.rotation.y;
    const startZ = animatedTubePart.rotation.z;
    const startFallbackBodyTwist = fallbackBodyTwist;

    animate({
      duration: TUBE_HANDLE_STOP_ANIMATION_MS,
      onUpdate: (progress) => {
        if (token !== tubeAnimationToken) return;

        const clamped = MathUtils.clamp(progress, 0, 1);
        const eased = clamped * clamped * (3 - 2 * clamped);
        fallbackBodyTwist = MathUtils.lerp(startFallbackBodyTwist, 0, eased);

        if (tubeHandle) {
          animatedTubePart.rotation.set(
            MathUtils.lerp(startX, baseHandleRotation.x, eased),
            MathUtils.lerp(startY, baseHandleRotation.y, eased),
            MathUtils.lerp(startZ, baseHandleRotation.z, eased)
          );
        }
      },
      onComplete: () => {
        if (token !== tubeAnimationToken) return;

        fallbackBodyTwist = 0;
        animatedTubePart.rotation.copy(baseHandleRotation);
        tubeState = "idle";
      }
    });
  };

  const clearAimTarget = () => {
    aimedRoot = null;
    aimStartedAtMs = 0;
  };

  const keepTubeFlowAlive = (nowMs: number) => {
    clearAimTarget();
    flowGraceUntilMs = nowMs + TUBE_FLOW_GRACE_PERIOD_MS;
  };

  const startTubeHandleAnimation = () => {
    if (tubeState !== "idle" && tubeState !== "closing") return;

    tubeState = "emitting";
    tubeAnimationToken += 1;
    const token = tubeAnimationToken;
    const startX = animatedTubePart.rotation.x;
    const startY = animatedTubePart.rotation.y;
    const startZ = animatedTubePart.rotation.z;
    const targetDelta = MathUtils.degToRad(
      tubeHandle ? TUBE_HANDLE_ROTATION_DEG : TUBE_FALLBACK_BODY_TWIST_DEG
    );
    const startFallbackBodyTwist = fallbackBodyTwist;
    const targetFallbackBodyTwist = MathUtils.degToRad(
      tubeHandle ? 3 : TUBE_FALLBACK_BODY_TWIST_DEG
    );

    startTubeFlowSound();

    animate({
      duration: TUBE_HANDLE_START_ANIMATION_MS,
      onUpdate: (progress) => {
        if (token !== tubeAnimationToken) return;

        const clamped = MathUtils.clamp(progress, 0, 1);
        const eased = clamped * clamped * (3 - 2 * clamped);

        if (tubeHandle) {
          animatedTubePart.rotation.set(
            startX,
            startY + targetDelta * eased,
            startZ
          );
        }
        fallbackBodyTwist = MathUtils.lerp(
          startFallbackBodyTwist,
          targetFallbackBodyTwist,
          eased
        );
      },
      onComplete: () => {
        if (token !== tubeAnimationToken) return;

        if (tubeHandle) {
          animatedTubePart.rotation.set(startX, startY + targetDelta, startZ);
        }
        fallbackBodyTwist = targetFallbackBodyTwist;
      }
    });
  };

  return {
    onExit: () => {
      stopTubeFlowSound();
      tubeParticles?.dispose();
      ambientFish.dispose();
      objects.forEach(disposeGarbageObject);
      objects.clear();
      objectsArray = [];
      hudGroup.remove(reticle.sprite);
      reticle.dispose();
      garbageHintTracker.dispose();
      plusOneTexture.dispose();
      plusOneMaterial.dispose();
      onExit();
    },
    onUpdate: () => {
      onUpdate();
      const frameNow = performance.now();
      const garbageDeltaSeconds = Math.min(
        (frameNow - lastGarbageUpdateTime) / 1000,
        0.05
      );
      lastGarbageUpdateTime = frameNow;
      const swayT = (frameNow - sceneStartedAt) / 1000;
      ambientFish.update(swayT, getAmbientRevealProgress(), getAmbientPollutionLevel());

      const driftX = Math.sin(swayT * 0.68) * 0.05 + Math.sin(swayT * 1.27 + 1.1) * 0.018;
      const driftY = Math.sin(swayT * 0.53 + 0.6) * 0.03 + Math.sin(swayT * 1.05 + 2.0) * 0.012;
      stick.position.x = driftX;
      stick.position.y = TUBE_BODY_BASE_Y + driftY;
      stick.position.z = TUBE_BODY_BASE_Z;
      stick.rotation.z = driftX * 0.7;
      stick.rotation.x =
        MathUtils.degToRad(TUBE_BODY_BASE_PITCH_DEG) +
        driftY * 0.5 +
        fallbackBodyTwist;
      objects.forEach((object) => {
        object.userData.updateWasteWater?.();
      });

      // Пауза факта не трогает render loop, но блокирует автосбор:
      // луч может продолжать смотреть на объект, счётчик при этом не растёт.
      if (useSceneStore.getState().isGamePaused) {
        garbageHintTracker.update(swayT);
        reticle.update(swayT, false);
        stopTubeFlow();
        tubeParticles?.update(stick, camera, false);
        return;
      }

      raycaster.setFromCamera(screenCenter, camera);

      const intersects = raycaster.intersectObjects(objectsArray, true);

      const hit = intersects.find((intersect) =>
        Boolean(getGarbageRoot(intersect.object))
      );
      const root = hit ? getGarbageRoot(hit.object) : null;
      const nowMs = performance.now();

      if (!root || !objects.has(root) || root.userData.hasHit) {
        if (tubeState === "emitting" && nowMs <= flowGraceUntilMs) {
          clearAimTarget();
        } else if (tubeState === "emitting" && flowGraceUntilMs === 0) {
          keepTubeFlowAlive(nowMs);
        } else if (tubeState === "closing") {
          clearAimTarget();
        } else {
          stopTubeFlow();
        }
      } else {
        if (aimedRoot !== root) {
          if (tubeState === "emitting" && nowMs <= flowGraceUntilMs) {
            clearAimTarget();
          } else if (tubeState === "closing") {
            tubeState = "idle";
            tubeAnimationToken += 1;
            clearAimTarget();
          } else {
            stopTubeFlow();
          }
          aimedRoot = root;
          aimStartedAtMs =
            tubeState === "emitting"
              ? nowMs - TUBE_AIM_HOLD_DURATION_MS
              : nowMs;
          flowGraceUntilMs = 0;
        }

        const isAimReady = nowMs - aimStartedAtMs >= TUBE_AIM_HOLD_DURATION_MS;

        if (isAimReady && tubeState === "idle") {
          startTubeHandleAnimation();
        }

        if (tubeState === "emitting") {
          let skipRemoval = false;
          if (root.userData.wasteWater) {
            const lastMs = root.userData._lastWasteDamageAt as
              | number
              | undefined;
            if (
              lastMs !== undefined &&
              nowMs - lastMs < WASTE_WATER_HIT_COOLDOWN_MS
            ) {
              skipRemoval = true;
            } else {
              root.userData._lastWasteDamageAt = nowMs;
              root.userData.health -= 1;
              root.userData.syncWasteWaterParticles?.();
              if (root.userData.health > 0) {
                skipRemoval = true;
              }
            }
          }

          if (!skipRemoval) {
            const {
              setCollectedGarbageCount,
              collectedGarbageCount,
              totalGarbageCount
            } = useSceneStore.getState();

            setCollectedGarbageCount(collectedGarbageCount + 1);

            if (collectedGarbageCount + 1 >= totalGarbageCount) {
              onWin();
            }

            root.userData.hasHit = true;
            showGarbageCollectionFeedback(scene, root);
            objects.delete(root);
            objectsArray.splice(objectsArray.indexOf(root), 1);
            if (root.userData.washAwayOnHit) {
              animateWashAwayGarbage(root, scene, () => {
                disposeGarbageObject(root);
              });
            } else {
              scene.remove(root);
              disposeGarbageObject(root);
            }
            if (objects.size > 0) {
              keepTubeFlowAlive(nowMs);
            } else {
              stopTubeFlow();
            }

            const sprite = new Sprite(plusOneMaterial.clone());
            sprite.scale.set(0.1, 0.1, 0.1);
            sprite.material.opacity = 0.5;
            sprite.position.copy(root.position);
            scene.add(sprite);

            const localScreenTopPos = sprite.position.clone();
            localScreenTopPos.y += 0.4;
            localScreenTopPos.x += Math.random() * 0.5 - 0.25;

            animate({
              duration: ANIMATION_DURATION * 5,
              onUpdate: (progress) => {
                sprite.position.lerpVectors(
                  sprite.position,
                  localScreenTopPos,
                  Math.pow(progress, 0.75)
                );
                sprite.material.opacity = Math.pow(1 - progress, 2);
              },
              onComplete: () => {
                scene.remove(sprite);
                sprite.material.dispose();
              }
            });
          }
        }
      }

      objects.forEach((object) => {
        updateFloatingGarbageObject(
          object,
          roomSize,
          baseCameraHeight,
          scene,
          camera,
          {
            enforceCameraComfortBand: false,
            floatMotion: false,
            deltaSeconds: garbageDeltaSeconds,
          }
        );
      });

      garbageHintTracker.update(swayT);
      reticle.update(
        swayT,
        Boolean(aimedRoot) || (tubeState === "emitting" && nowMs <= flowGraceUntilMs)
      );

      tubeParticles?.update(
        stick,
        camera,
        tubeState === "emitting" &&
          (Boolean(aimedRoot && nowMs - aimStartedAtMs >= TUBE_AIM_HOLD_DURATION_MS) ||
            nowMs <= flowGraceUntilMs)
      );
    }
  };
};
